import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, XCircle, ShieldCheck, User, Building2,
  Briefcase, Loader2, Clock, Mail, UserCheck, UserX, Send,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useHosts, useCreateHost, useCheckHostEmail } from "@/hooks/useHosts";
import { useUnits } from "@/hooks/useUnits";
import { useAuth } from "@/contexts/AuthContext";

export default function AddHost() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: units = [] } = useUnits();
  const { data: hosts = [] } = useHosts();
  const createHost = useCreateHost();

  const [form, setForm] = useState({
    email: "",
    unit_id: "",
    registration_number: "",
    night_limit: "90",
    host_type: "principal_residence",
    business_licence_number: "",
    business_licence_status: "not_required",
  });

  // Live email check — runs whenever email looks valid
  const emailCheck = useCheckHostEmail(form.email);

  const set = (field: string, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  // Units that don't already have a host
  const existingUnitIds = new Set(hosts.map((h: any) => h.unit_id));
  const availableUnits = units.filter((u: any) => !existingUnitIds.has(u.id));

  const resolvedUserId = emailCheck.data?.found
    ? emailCheck.data.userId
    : user!.id;

  const canSubmit =
    !!form.unit_id &&
    !!user?.id &&
    !createHost.isPending &&
    !emailCheck.isFetching;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createHost.mutate(
      {
        user_id: resolvedUserId,
        unit_id: form.unit_id,
        registration_number: form.registration_number.trim() || undefined,
        night_limit: parseInt(form.night_limit) || 90,
        host_type: form.host_type,
        business_licence_number: form.business_licence_number.trim() || undefined,
        business_licence_status: form.business_licence_status,
      },
      {
        onSuccess: () => navigate("/hosts"),
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/hosts")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Add New Host</h1>
          <p className="text-sm text-muted-foreground">
            Fill in the host details below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        {/* ── Section 0: Host Identity (email lookup) ──────────────────── */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              Host Identity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="host_email">Host's Email Address</Label>
              <div className="relative">
                <Input
                  id="host_email"
                  type="email"
                  placeholder="host@example.com"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={`pr-32 ${
                    emailCheck.data?.found
                      ? "border-emerald-400 focus-visible:ring-emerald-400"
                      : ""
                  }`}
                />
                {/* Inline status badge */}
                {emailCheck.isFetching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                {!emailCheck.isFetching && emailCheck.data?.found && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Badge variant="outline" className="text-[10px] border-emerald-400 text-emerald-700 bg-emerald-50 gap-1 px-2">
                      <UserCheck className="h-3 w-3" />
                      {emailCheck.data.fullName ?? "User found"}
                    </Badge>
                  </div>
                )}
                {!emailCheck.isFetching && emailCheck.data && !emailCheck.data.found && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50 gap-1 px-2">
                      <Send className="h-3 w-3" />
                      Will invite
                    </Badge>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {emailCheck.data?.found
                  ? `✓ Stratakey account found — this host will be linked directly (no invitation email needed).`
                  : emailCheck.data && !emailCheck.data.found
                  ? `No Stratakey account found. The host will receive an invitation email to join.`
                  : `Enter the host's email to check if they already have a Stratakey account.`}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 1: Unit Assignment ──────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              Unit Assignment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="unit_id">Unit *</Label>
              {units.length === 0 ? (
                <div className="rounded-md border border-dashed px-4 py-3 text-center">
                  <p className="text-sm text-muted-foreground">No units have been registered yet.</p>
                  <Button
                    variant="link"
                    className="h-auto p-0 text-xs mt-1"
                    onClick={() => navigate("/units")}
                  >
                    Go to Unit Management to add units →
                  </Button>
                </div>
              ) : availableUnits.length === 0 ? (
                <div className="rounded-md border border-dashed px-4 py-3 text-center">
                  <p className="text-sm text-muted-foreground">All registered units already have a host.</p>
                </div>
              ) : (
                <Select value={form.unit_id} onValueChange={(v) => set("unit_id", v)}>
                  <SelectTrigger id="unit_id">
                    <SelectValue placeholder="Select a unit..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUnits.map((u: any) => (
                      <SelectItem key={u.id} value={u.id}>
                        Unit {u.unit_number} — {u.owner_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="night_limit">Night Limit (per year)</Label>
              <Input
                id="night_limit"
                type="number"
                min={1}
                max={365}
                value={form.night_limit}
                onChange={(e) => set("night_limit", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* ── Section 2: STR Registration Number — Coming Soon ────────── */}
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              Registry Verification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-6 text-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Clock className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <p className="font-semibold text-sm">Coming Soon</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-[220px]">
                  Coming soon.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 3: Host Type ────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              Host Type
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Host Type *</Label>
              <Select value={form.host_type} onValueChange={(v) => set("host_type", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="principal_residence">Principal Residence</SelectItem>
                  <SelectItem value="non_principal_residence">Non-Principal Residence</SelectItem>
                  <SelectItem value="fractional_ownership">Fractional Ownership</SelectItem>
                  <SelectItem value="strata_hotel">Strata Hotel</SelectItem>
                  <SelectItem value="seasonal_exempt">Seasonal Accommodation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 4: Business Licence ────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary" />
              Business Licence
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="biz_licence">Business Licence Number</Label>
              <Input
                id="biz_licence"
                placeholder="e.g. BL-2024-01234"
                value={form.business_licence_number}
                onChange={(e) => set("business_licence_number", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Business Licence Status</Label>
              <Select value={form.business_licence_status} onValueChange={(v) => set("business_licence_status", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_required">Not Required</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="valid">Valid</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.business_licence_status === "revoked" && (
              <Alert variant="destructive" className="py-2">
                <XCircle className="h-3.5 w-3.5" />
                <AlertDescription className="text-xs">
                  A revoked business licence will result in the host's registration being cancelled.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Submit Bar ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between rounded-lg border bg-card px-6 py-4 shadow-sm">
        <p className="text-sm text-muted-foreground">
          {!form.unit_id
            ? "Select a unit to continue."
            : emailCheck.data?.found
            ? `Ready — will link ${emailCheck.data.fullName ?? emailCheck.data.email} directly.`
            : emailCheck.data && !emailCheck.data.found
            ? `Ready — will send invitation to ${form.email}.`
            : "Ready to submit."}
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate("/hosts")}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createHost.isPending ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding...</>
            ) : (
              "Add Host"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
